import React from "react";
import { Redirect } from "react-router";
import { Route, Switch } from "react-router-dom";
import { useSetApiMode } from "../hooks/useSetApiMode";
import { AcceptInvitation } from "../pages/auth/AcceptInvitationPage";
import { Complete } from "../pages/get-compliant/Complete";
import { AdditionalPrivacyLanguagePage } from "../pages/get-compliant/consumer-collection/AdditionalPrivacyLanguagePage";
import { DataRetentionCookieScanner } from "../pages/get-compliant/consumer-collection/ConsumerCollectionCookieScanner";
import { ConsumerCollectionGdprLawfulBases } from "../pages/get-compliant/consumer-collection/ConsumerCollectionGdprLawfulBases";
import { ConsumerCollectionPurposes } from "../pages/get-compliant/consumer-collection/ConsumerCollectionPurposes";
import { ConsumerDataCollection } from "../pages/get-compliant/consumer-collection/ConsumerDataCollection";
import { ConsumerDataCollectionForGroup } from "../pages/get-compliant/consumer-collection/ConsumerDataCollectionForGroup";
import { CorePrivacyConceptsPage } from "../pages/get-compliant/consumer-collection/CorePrivacyConceptsPage";
import { GDPRInternationalDataTransfers } from "../pages/get-compliant/consumer-collection/GDPRInternationalDataTransfers";
import { CookiePolicyPage } from "../pages/get-compliant/CookiePolicyPage";
import { ConsumerDataSourcesPage } from "../pages/get-compliant/data-map/ConsumerDataSourcesPage";
import { AddDataRecipients } from "../pages/get-compliant/data-recipients/AddDataRecipients";
import { ContractorsPage } from "../pages/get-compliant/data-recipients/ContractorsPage";
import { ConsumerDataDisclosuresPage } from "../pages/get-compliant/data-map/ConsumerDataDisclosuresPage";
import { Platforms as DataRecipientPlatforms } from "../pages/get-compliant/data-recipients/Platforms";
import { VendorSelling as DataRecipientVendorSelling } from "../pages/get-compliant/data-recipients/Selling";
import { VendorSharing as DataRecipientVendorSharing } from "../pages/get-compliant/data-recipients/Sharing";
import { ThirdPartyRecipientsPage } from "../pages/get-compliant/data-recipients/ThirdPartyRecipientsPage";
import { DataMapIntroductionPage } from "../pages/get-compliant/DataMapIntroductionPage";
import { EmployeePrivacyNoticeReview } from "../pages/get-compliant/employee-collection/EmployeePrivacyNoticeReview";
import {
  GetCompliantDashboardPage,
  GetCompliantInviteUsersPage,
} from "../pages/get-compliant/GetCompliantDashboardPage";
import { IntegrationsPage } from "../pages/get-compliant/IntegrationsPage";
import { WebsiteSetupPage } from "../pages/get-compliant/privacy-center/WebsiteSetupPage";
import { EmailSetupPage } from "../pages/get-compliant/privacy-center/EmailSetupPage";
import { PrivacyNoticeChecklist } from "../pages/get-compliant/PrivacyNoticeChecklist";
import { PrivacyNoticeReview } from "../pages/get-compliant/PrivacyNoticeReview";
import { Settings } from "../pages/get-compliant/Settings";
import { Phase } from "../util/Phase";
import { FourOhFourRoutes } from "./FourOhFourRoutes";
import { useGetCompliantContext } from "./GetCompliantContext";
import { Routes } from "./routes";
import { SetupRoutes } from "./SetupRoutes";
import { VendorAssociationForGroup } from "../pages/get-compliant/data-recipients/VendorAssociationForGroup";
import { DataRecipientAssociation } from "../pages/get-compliant/data-recipients/DataRecipientAssociation";
import { InternalStoragePage } from "../pages/get-compliant/data-recipients/InternalStoragePage";
import { ShopifyIntegrationPage } from "../pages/integrations/ShopifyIntegrationPage";
import { VerifySubdomainPage } from "../pages/get-compliant/privacy-center/VerifySubdomainPage";
import { BusinessSurveyAquiloPage } from "../pages/surveys/BusinessSurveyAquiloPage";
import { SurveyAnswersReviewPage } from "../pages/get-compliant/SurveyAnswersReviewPage";
import { ProcessingRegion } from "../pages/get-compliant/data-recipients/ProcessingRegion";

type GetCompliantPageRouterProps = {
  phase: Phase;
};

export const GetCompliantPageRouter = ({ phase }: GetCompliantPageRouterProps) => {
  const { progress } = useGetCompliantContext();
  useSetApiMode("Draft", "GetCompliantPageRouter");
  const defaultRedirectTo = phase === "SETUP" ? Routes.setup.root : Routes.getCompliant.root;
  return (
    <Switch>
      <Route exact path={Routes.getCompliant.dashboard}>
        <GetCompliantDashboardPage />
      </Route>
      <Route exact path={Routes.getCompliant.inviteUsers}>
        <GetCompliantInviteUsersPage />
      </Route>

      <Route exact path={Routes.getCompliant.businessSurveyAquilo}>
        <BusinessSurveyAquiloPage />
      </Route>
      <Route exact path={Routes.getCompliant.surveyAnswersReview}>
        <SurveyAnswersReviewPage />
      </Route>

      <Route exact path={Routes.getCompliant.dataMap.introduction}>
        <DataMapIntroductionPage />
      </Route>

      <Route exact path={Routes.getCompliant.businessSurvey.consumerDataCollection}>
        <ConsumerDataCollection />
      </Route>
      <Route exact path={Routes.getCompliant.businessSurvey.consumerDataCollectionForGroup}>
        <ConsumerDataCollectionForGroup />
      </Route>

      <Route exact path={Routes.getCompliant.dataCollection.root}>
        <Redirect to={Routes.getCompliant.dataCollection.consumerCollection} />
      </Route>
      <Route exact path={Routes.getCompliant.dataCollection.consumerCollection}>
        <Redirect to={Routes.getCompliant.dataCollection.consumerPurposes} />
      </Route>
      <Route exact path={Routes.getCompliant.dataCollection.gdprLawfulBases}>
        <ConsumerCollectionGdprLawfulBases currentProgress={progress} />
      </Route>

      <Route
        exact
        path={Routes.getCompliant.dataMap.consumerSources}
        component={ConsumerDataSourcesPage}
      />

      <Route exact path={Routes.getCompliant.dataCollection.consumerPurposes}>
        <ConsumerCollectionPurposes currentProgress={progress} />
      </Route>
      <Route exact path={Routes.getCompliant.dataCollection.corePrivacyConcepts}>
        <CorePrivacyConceptsPage currentProgress={progress} />
      </Route>

      {/* Properly redirect the SC-style integrations link from the Dev Instructions page if org is still in GC. */}
      <Route exact path={Routes.privacyCenter.integrations}>
        <Redirect to={Routes.getCompliant.dataRetention.vendorIntegrations} />
      </Route>
      <Route exact path={Routes.getCompliant.dataRetention.vendorIntegrations}>
        <IntegrationsPage />
      </Route>

      <Route exact path={Routes.getCompliant.dataRetention.gdprCookieScanner}>
        <DataRetentionCookieScanner currentProgress={progress} />
      </Route>
      <Route exact path={Routes.getCompliant.dataRetention.cookiePolicy}>
        <CookiePolicyPage />
      </Route>

      <Route exact path={Routes.getCompliant.dataCollection.additionalPrivacyLanguage}>
        <AdditionalPrivacyLanguagePage />
      </Route>

      <Route exact path={Routes.getCompliant.dataCollection.internationalDataTransfers}>
        <GDPRInternationalDataTransfers />
      </Route>

      <Route
        exact
        path={[Routes.getCompliant.employeeMap.employeeReviewForGroup]}
        component={EmployeePrivacyNoticeReview}
      />

      <Route exact path={Routes.getCompliant.dataRecipients.root}>
        <AddDataRecipients currentProgress={progress} />
      </Route>

      <Route exact path={Routes.getCompliant.dataRecipients.platforms}>
        <DataRecipientPlatforms currentProgress={progress} />
      </Route>

      <Route exact path={Routes.getCompliant.dataRecipients.vendorAssociation}>
        <DataRecipientAssociation />
      </Route>
      <Route exact path={Routes.getCompliant.dataRecipients.vendorAssociationForGroup}>
        <VendorAssociationForGroup />
      </Route>

      <Route
        exact
        path={Routes.getCompliant.dataRecipients.internalStorage}
        component={InternalStoragePage}
      />

      <Route
        exact
        path={Routes.getCompliant.dataRecipients.dataDisclosures}
        component={ConsumerDataDisclosuresPage}
      />

      <Route
        exact
        path={Routes.getCompliant.dataRecipients.contractors}
        component={ContractorsPage}
      />
      <Route
        exact
        path={Routes.getCompliant.dataRecipients.thirdPartyRecipients}
        component={ThirdPartyRecipientsPage}
      />

      <Route exact path={Routes.getCompliant.dataRecipients.processingRegion}>
        <ProcessingRegion />
      </Route>
      <Route exact path={Routes.getCompliant.dataRecipients.sharing}>
        <DataRecipientVendorSharing />
      </Route>
      <Route exact path={Routes.getCompliant.dataRecipients.selling}>
        <DataRecipientVendorSelling />
      </Route>

      <Route exact path={Routes.getCompliant.privacyNotices.caPrivacyNotice}>
        <PrivacyNoticeReview currentProgress={progress} noticeType="CA_PRIVACY_NOTICE" />
      </Route>
      <Route exact path={Routes.getCompliant.privacyNotices.consumerGroup}>
        <PrivacyNoticeReview currentProgress={progress} noticeType="CONSUMER_GROUP" />
      </Route>
      <Route exact path={Routes.getCompliant.publishNotices.root}>
        <PrivacyNoticeChecklist currentProgress={progress} />
      </Route>

      <Route exact path={Routes.getCompliant.privacyRequests.privacyCenter}>
        <Redirect to={Routes.getCompliant.privacyCenter.emailSetup} />
      </Route>
      <Route exact path={Routes.getCompliant.privacyCenter.websiteSetup}>
        <WebsiteSetupPage />
      </Route>
      <Route exact path={Routes.getCompliant.privacyCenter.emailSetup}>
        <EmailSetupPage />
      </Route>
      <Route exact path={Routes.getCompliant.privacyCenter.verifySubdomain}>
        <VerifySubdomainPage />
      </Route>

      <Route exact path={Routes.getCompliant.complete.root}>
        <Complete />
      </Route>
      <Route exact path={Routes.getCompliant.settings.user}>
        <Settings currentProgress={progress} />
      </Route>
      <Route exact path={Routes.getCompliant.settings.root}>
        <Redirect to={Routes.getCompliant.settings.user} />
      </Route>
      <Route exact path={Routes.getCompliant.root}>
        <Redirect to={Routes.getCompliant.dashboard} />
      </Route>
      <Route exact path={Routes.invite} component={AcceptInvitation} />
      <Route exact path={Routes.requests.active}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.login}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.auth.mfa}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.auth.reset}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.auth.set}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.root}>
        <Redirect to={defaultRedirectTo} />
      </Route>

      <Route exact path={Routes.integrations.shopify} render={() => <ShopifyIntegrationPage />} />
      <SetupRoutes phase={phase} />
      <FourOhFourRoutes />
    </Switch>
  );
};
